// Calm SaaS theme — ported from design prototype.
// Colours live in CSS custom properties so light/dark switches without a
// re-render: `theme.bg` is 'var(--bg)', and the palettes below fill them in.

const light = {
  bg:        '#f7f6f4',
  surface:   '#ffffff',
  surface2:  '#fafaf8',
  border:    '#ececea',
  borderHi:  '#dcdcd8',
  text:      '#1d1e22',
  textDim:   '#5e616a',
  textMuted: '#9396a0',
  accent:    '#4d5bd1',
  accentInk: '#ffffff',

  status: {
    ok:      { bg: '#e4f0e6', fg: '#356b41', dot: '#4d9b5c' },
    warn:    { bg: '#f5ead0', fg: '#735012', dot: '#c79a35' },
    danger:  { bg: '#f4dadc', fg: '#8a3035', dot: '#c75058' },
    service: { bg: '#dfe4f0', fg: '#3b4a76', dot: '#5b6db0' },
    muted:   { bg: '#ececea', fg: '#6b6e78', dot: '#9da0a8' },
  },
};

type Palette = typeof light;

const dark: Palette = {
  bg:        '#0e0f12',
  surface:   '#17181c',
  surface2:  '#1c1d22',
  border:    '#27292f',
  borderHi:  '#363941',
  text:      '#edeef0',
  textDim:   '#9ea0a8',
  textMuted: '#6c6e76',
  accent:    '#9aa6ff',
  accentInk: '#14162b', // the dark accent is light — white text on it is unreadable

  status: {
    ok:      { bg: '#172a1f', fg: '#9bd3a6', dot: '#4d9b5c' },
    warn:    { bg: '#2e2410', fg: '#e6c47b', dot: '#c79a35' },
    danger:  { bg: '#321a1d', fg: '#e89a9c', dot: '#c75058' },
    service: { bg: '#1a2332', fg: '#9eb1d9', dot: '#5b6db0' },
    muted:   { bg: '#22232a', fg: '#9b9da6', dot: '#9da0a8' },
  },
};

type Vars<T> = { [K in keyof T]: T[K] extends string ? string : Vars<T[K]> };

/** { status: { ok: { bg } } } → { status: { ok: { bg: 'var(--status-ok-bg)' } } } */
function asVars<T extends object>(p: T, prefix = '-'): Vars<T> {
  return Object.fromEntries(Object.entries(p).map(([k, v]) =>
    [k, typeof v === 'string' ? `var(${prefix}-${k})` : asVars(v as object, `${prefix}-${k}`)],
  )) as Vars<T>;
}

/** `--bg: #f7f6f4; --status-ok-bg: …;` */
function declarations(p: object, prefix = '-'): string {
  return Object.entries(p).map(([k, v]) =>
    typeof v === 'string' ? `${prefix}-${k}:${v};` : declarations(v as object, `${prefix}-${k}`),
  ).join('');
}

export const theme = {
  name: 'calm' as const,
  font: '"Inter", system-ui, -apple-system, sans-serif',
  ...asVars(light),
};

/** A theme colour at `pct`% opacity (CSS vars can't take a hex alpha suffix). */
export const alpha = (color: string, pct: number) => `color-mix(in srgb, ${color} ${pct}%, transparent)`;

/**
 * The palette CSS. `data-theme` on <html> forces light or dark; without it
 * the OS setting decides (see lib/themePref.ts and the boot script in index.html).
 */
export function installThemeCss() {
  const css = `
:root{color-scheme:light;${declarations(light)}}
:root[data-theme="dark"]{color-scheme:dark;${declarations(dark)}}
@media (prefers-color-scheme: dark){:root:not([data-theme="light"]){color-scheme:dark;${declarations(dark)}}}
html,body{background:var(--bg);color:var(--text);}`;
  const style = document.createElement('style');
  style.id = 'theme-vars';
  style.textContent = css;
  document.head.appendChild(style);
}

export type Theme = typeof theme;
export type StatusTone = keyof typeof theme.status;
